import { useState } from "react";
import { useTestimonials, Testimonial } from "@/hooks/useTestimonials";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus, Star } from "lucide-react";
import { TestimonialCard } from "./TestimonialCard";

export function TestimonialsManager() {
  const { testimonials, isLoading, createTestimonial, updateTestimonial, deleteTestimonial } = useTestimonials(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    author_name: "",
    testimonial_text: "",
    rating: 5,
    author_role: "",
    author_photo_url: "",
    is_active: true,
    display_order: 0,
  });

  const handleEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      author_name: testimonial.author_name,
      testimonial_text: testimonial.testimonial_text,
      rating: testimonial.rating,
      author_role: testimonial.author_role || "",
      author_photo_url: testimonial.author_photo_url || "",
      is_active: testimonial.is_active,
      display_order: testimonial.display_order,
    });
  };

  const handleSave = () => {
    if (editingId) {
      updateTestimonial({
        id: editingId,
        updates: formData,
      });
      setEditingId(null);
    } else if (isCreating) {
      createTestimonial(formData);
      setIsCreating(false);
    }
    resetForm();
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      author_name: "",
      testimonial_text: "",
      rating: 5,
      author_role: "",
      author_photo_url: "",
      is_active: true,
      display_order: testimonials.length,
    });
  };

  const handleToggleActive = (id: string, currentActive: boolean) => {
    updateTestimonial({
      id,
      updates: { is_active: !currentActive },
    });
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteTestimonial(deleteId);
      setDeleteId(null);
    }
  };

  const isFormValid = () => {
    return (
      formData.author_name.length >= 2 &&
      formData.author_name.length <= 100 &&
      formData.testimonial_text.length >= 10 &&
      formData.testimonial_text.length <= 500 &&
      formData.rating >= 1 &&
      formData.rating <= 5
    );
  };

  if (isLoading) {
    return <div className="p-6">Carregando depoimentos...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Gerenciar Depoimentos</h2>
        <Button onClick={() => setIsCreating(true)} disabled={isCreating || editingId !== null}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Depoimento
        </Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-green-300 bg-green-50">
          <CardHeader>
            <CardTitle>{editingId ? "Editar Depoimento" : "Novo Depoimento"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="author_name">
                  Nome do Autor* ({formData.author_name.length}/100)
                </Label>
                <Input
                  id="author_name"
                  value={formData.author_name}
                  onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                  placeholder="Ex: Maria Santos"
                  maxLength={100}
                />
              </div>
              <div>
                <Label htmlFor="author_role">Função/Descrição (opcional)</Label>
                <Input
                  id="author_role"
                  value={formData.author_role}
                  onChange={(e) => setFormData({ ...formData, author_role: e.target.value })}
                  placeholder="Ex: Paciente desde 2023"
                  maxLength={100}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="testimonial_text">
                Depoimento* ({formData.testimonial_text.length}/500)
              </Label>
              <Textarea
                id="testimonial_text"
                value={formData.testimonial_text}
                onChange={(e) => setFormData({ ...formData, testimonial_text: e.target.value })}
                placeholder="Digite o depoimento aqui..."
                rows={4}
                maxLength={500}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rating">Avaliação (1-5 estrelas)</Label>
                <div className="flex items-center gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-6 h-6 cursor-pointer ${
                        star <= formData.rating
                          ? "text-yellow-400 fill-current"
                          : "text-gray-300"
                      }`}
                      onClick={() => setFormData({ ...formData, rating: star })}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="author_photo_url">URL da Foto (opcional)</Label>
                <Input
                  id="author_photo_url"
                  value={formData.author_photo_url}
                  onChange={(e) => setFormData({ ...formData, author_photo_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Ativo (visível no site)</Label>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-2">Preview:</p>
              <div className="max-w-md">
                <TestimonialCard
                  authorName={formData.author_name || "Nome do Autor"}
                  testimonialText={formData.testimonial_text || "Texto do depoimento aparecerá aqui..."}
                  rating={formData.rating}
                  authorRole={formData.author_role}
                  authorPhotoUrl={formData.author_photo_url}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!isFormValid()}>
                Salvar
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 max-w-md">
                  <TestimonialCard
                    authorName={testimonial.author_name}
                    testimonialText={testimonial.testimonial_text}
                    rating={testimonial.rating}
                    authorRole={testimonial.author_role}
                    authorPhotoUrl={testimonial.author_photo_url}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={testimonial.is_active ? "default" : "secondary"}>
                      {testimonial.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                    <Badge variant="outline">Ordem: {testimonial.display_order}</Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={testimonial.is_active}
                      onCheckedChange={() => handleToggleActive(testimonial.id, testimonial.is_active)}
                    />
                    <span className="text-sm">Visível</span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(testimonial)}
                      disabled={editingId !== null || isCreating}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteId(testimonial.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este depoimento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
